import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Shield, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function LoginPage() {
  const { refresh, setUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const showDemo = (process.env.REACT_APP_SHOW_DEMO || "").trim().toLowerCase() === "true";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(data.onboarding_completed ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Anmeldung fehlgeschlagen"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleDemo = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/demo/login");
      setUser(data);
      toast.success("Willkommen im Demo-Modus, Stefan.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Demo-Modus nicht verfügbar"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-ag-paper ag-paper-grain">
      {/* Left hero */}
      <div className="hidden lg:flex w-1/2 ag-sidebar text-slate-100 flex-col justify-between p-14 relative overflow-hidden">
        <div className="absolute -bottom-20 -right-20 h-96 w-96 rounded-full bg-amber-400/10 blur-3xl" />
        <div className="relative">
          <Logo variant="full" tone="dark" className="ag-logo-enter h-28 w-auto -ml-3" />
        </div>
        <div className="relative max-w-md">
          <Shield className="h-7 w-7 text-amber-300 mb-6" strokeWidth={1.3} />
          <p className="font-serif text-3xl leading-snug text-white/95">
            Sie kaufen keine Software. Sie kaufen das gute Gefühl, dass alles geregelt ist.
          </p>
          <p className="text-sm text-slate-400 mt-6">
            Ihre digitale Vertrauensplattform fürs Leben. Lebensbaum, Vertrauensring und sicherer Dokumententresor.
          </p>
        </div>
        <div className="relative text-xs text-slate-500">© ALLES GEREGELT · {new Date().getFullYear()}</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Logo + Claim — first focal point on mobile, secondary anchor on desktop */}
          <div className="flex flex-col items-center text-center mb-10 lg:hidden">
            <Logo variant="full" tone="light" className="ag-logo-enter h-20 w-auto" />
            <div className="ag-claim-enter mt-4 text-sm text-slate-500 italic" data-testid="brand-claim">
              Ein gutes Gefühl. Weil alles geregelt ist.
            </div>
          </div>
          <h1 className="font-serif text-4xl text-ag-ink mb-2">{t.auth.welcome}</h1>
          <p className="text-sm text-slate-500 mb-2">{t.auth.login} · {t.auth.email} & {t.auth.password}</p>
          <p className="hidden lg:block text-sm text-slate-500 italic mb-6" data-testid="brand-claim-desktop">
            Ein gutes Gefühl. Weil alles geregelt ist.
          </p>

          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">{t.auth.email}</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-1.5 h-11 bg-white border-slate-200"
                placeholder="ihre.adresse@beispiel.de"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">{t.auth.password}</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-1.5 h-11 bg-white border-slate-200"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 bg-ag-ink hover:bg-slate-800 text-white rounded-xl"
            >
              {t.auth.submitLogin} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <div className="pt-1 text-right">
              <Link to="/passwort-vergessen" data-testid="login-forgot" className="text-xs text-slate-500 hover:text-ag-ink underline-offset-4 hover:underline">
                {t.reset.forgotLink}
              </Link>
            </div>
          </form>

          <div className="flex items-center gap-3 my-5 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" />
            <span>{t.auth.orDivider}</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button
            type="button" variant="outline" onClick={handleGoogle}
            data-testid="login-google"
            className="w-full h-11 border-slate-200 bg-white hover:bg-slate-50 rounded-xl"
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t.auth.google}
          </Button>

          {showDemo && (
          <div className="mt-6 ag-card p-5 border-amber-200 bg-amber-50/40" data-testid="login-demo-card">
            <div className="flex items-start gap-3">
              <Sparkles className="h-4 w-4 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-ag-ink">{t.auth.demoTitle}</div>
                <div className="text-xs text-slate-600 mt-0.5">{t.auth.demoSub}</div>
                <Button
                  type="button" variant="link"
                  onClick={handleDemo}
                  data-testid="login-demo"
                  className="text-amber-700 hover:text-amber-800 px-0 h-7 mt-1"
                >
                  {t.auth.demoStart} →
                </Button>
              </div>
            </div>
          </div>
          )}

          <div className="mt-6 text-sm text-slate-500 text-center">
            {t.auth.noAccount}{" "}
            <Link to="/register" data-testid="link-register" className="text-ag-ink underline-offset-4 hover:underline font-medium">
              {t.auth.register}
            </Link>
          </div>
          <nav className="mt-12 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-slate-400" data-testid="login-legal-links">
            <Link to="/sicherheit" className="hover:text-ag-ink transition-colors">{t.footer.security}</Link>
            <Link to="/datenschutz" className="hover:text-ag-ink transition-colors">{t.footer.privacy}</Link>
            <Link to="/impressum" className="hover:text-ag-ink transition-colors">{t.footer.imprint}</Link>
          </nav>
        </div>
      </div>
    </div>
  );
}
